import { DateTime } from "luxon";
import {
  PathPickOrder,
  PathPickOrderItem,
} from "@/features/pathpick/features/order-history/types/response";

// 주문 상품의 대표 이미지 — 원본이 없으면 SKU(AI 수집) 이미지로 대신한다.
// 상품 찾기의 getProductImageUrl 과 같은 규칙이라 같은 상품이 두 화면에서 같게 보인다.
export const getPathPickOrderItemImageUrl = (item?: PathPickOrderItem) =>
  item?.imageUrls?.[0] ?? item?.skuImageUrls?.[0];

// 위 대표 이미지가 AI 수집 이미지인지 — AI 배지를 달지 판단한다.
export const isPathPickOrderItemImageAiCollected = (item?: PathPickOrderItem) =>
  !item?.imageUrls?.[0] && !!item?.skuImageUrls?.[0];

// "2026-07-21T00:57:10Z" → "2026. 07. 21."
export const formatPathPickOrderDate = (iso: string) =>
  DateTime.fromISO(iso).setLocale("ko").toFormat("yyyy. MM. dd.");

// "2026-08-22T08:10:00Z" → "2026. 08. 22. 오전 08:10"
// 상세 헤더/입금 기한처럼 시각까지 보여야 하는 자리에 쓴다.
// 로케일을 ko로 고정하는 것은 formatPathPickOrderDate와 동일한 기존 방식이다.
export const formatPathPickOrderDateTime = (iso: string) =>
  DateTime.fromISO(iso).setLocale("ko").toFormat("yyyy. MM. dd. a hh:mm");

// 주문 종료일 필터는 선택한 날짜 하루를 전부 포함해야 한다.
// 로컬 타임존 기준 23:59:59.999로 올린 뒤 qs가 UTC ISO로 직렬화한다
// (api-order의 Date.UTC(...23,59,59,999) 패턴은 로컬 getter와 UTC 생성을 섞어
//  KST에서 하루보다 넓게 잡히므로 따르지 않는다).
export const toPathPickOrderEndOfDay = (date: Date) =>
  DateTime.fromJSDate(date).endOf("day").toJSDate();

// 수량 확정 여부. 확정 전에도 total_confirmed_*가 0으로 내려오므로
// 값만으로는 "확정 0"과 "아직 미확정"을 구분할 수 없다. 확정 시각이 유일한 판별 근거다.
export const isPathPickOrderQuantityConfirmed = (order: PathPickOrder) =>
  Boolean(order.quantityConfirmedAt);

// 목록/상세 공통 — 확정 기준(confirmed)을 먼저 쓰고 없으면 주문 기준(ordered)으로 폴백한다.
// 값이 아예 없으면 null을 돌려주고 화면이 "-"로 표기한다.
export const selectPathPickOrderQuantity = (order: PathPickOrder) =>
  (isPathPickOrderQuantityConfirmed(order)
    ? order.totalConfirmedQuantity ?? order.totalOrderedQuantity
    : order.totalOrderedQuantity) ?? null;

export const selectPathPickOrderAmount = (order: PathPickOrder) =>
  (isPathPickOrderQuantityConfirmed(order)
    ? order.totalConfirmedAmount ?? order.totalOrderedAmount
    : order.totalOrderedAmount) ?? null;

// 목록 표기용 — 확정 후에는 주문값(취소선)과 확정값을 함께 보여준다.
// 취소 주문은 quantityConfirmedAt이 비어 있어 확정 줄 없이 주문값만 나간다.
// 확정값이 비어 오면 주문값으로 폴백해 "취소선만 있고 확정값이 '-'"인 화면을 막는다.
export const selectPathPickOrderTotalsView = (order: PathPickOrder) => ({
  orderedQuantity: order.totalOrderedQuantity ?? null,
  orderedAmount: order.totalOrderedAmount ?? null,
  confirmed: isPathPickOrderQuantityConfirmed(order)
    ? {
        quantity:
          order.totalConfirmedQuantity ?? order.totalOrderedQuantity ?? null,
        amount: order.totalConfirmedAmount ?? order.totalOrderedAmount ?? null,
      }
    : null,
});
